// src/components/plan/StripeCardForm.jsx
// Pooled inbox SP5 (spec § Testing — the first Stripe-form pattern; owner
// item 21: Stripe's standard form, styled dark). The ONLY file in src/ that
// imports @stripe/react-stripe-js or @stripe/stripe-js, so every screen test
// mocks this one module and nothing else of Stripe's.
//
//   <StripeCardForm clientSecret={secret}>
//     {({ field, complete, confirm }) => …}
//   </StripeCardForm>
//
// `ready` is true once the form has drawn; `loadError` is the unreachable
// message when Stripe's script or form could not load (else null); submit
// stays disabled until `ready`.
// `field` is Stripe's Payment Element (card number, expiry, CVC, country, ZIP;
// Stripe's own validation messages and 3-D Secure modal). `complete` is true
// once Stripe says the card is filled in. `confirm(kind)` confirms the intent
// behind `clientSecret` — `'setup'` for a SetupIntent (the card saved for $0),
// `'payment'` for a PaymentIntent (the meeting add-on) — and resolves to
// `{ ok: true, intent }` or `{ ok: false, error: { type, code, message } }`
// (Stripe's error, so a decline carries Stripe's own message). It never
// redirects unless the card's bank insists (`redirect: 'if_required'`).
import React, { useCallback, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { STRIPE_UNREACHABLE } from "./planCopy";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

let stripePromise = null;
// Loaded on first use, not at import, so importing a screen loads no script.
const stripeOnce = () => {
  if (!stripePromise) {
    // A failed load (script blocked, offline, no key) is not cached: the next
    // mount tries again.
    const attempt = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
    stripePromise = attempt;
    attempt.catch(() => {
      if (stripePromise === attempt) stripePromise = null;
    });
  }
  return stripePromise;
};

// Stripe draws the form inside its own frame, where our CSS variables do not
// reach, so the theme's values are read from the page the form sits in (dark
// under a `.stars-dark` wrapper, light on a legacy page) and handed over as
// Stripe's appearance variables. No colour is written here: every value comes
// from tokens.css.
const token = (node, name) =>
  getComputedStyle(node).getPropertyValue(`--stars-${name}`).trim();
const appearanceFrom = (node) => ({
  theme: node.closest(".stars-dark") ? "night" : "stripe",
  variables: {
    colorPrimary: token(node, "action"),
    colorBackground: token(node, "surface"),
    colorText: token(node, "text"),
    colorTextSecondary: token(node, "text-muted"),
    colorDanger: token(node, "danger"),
    fontFamily: token(node, "font"),
    borderRadius: token(node, "radius-md"),
  },
});

const asError = (error) => ({
  ok: false,
  error: {
    type: error?.type ?? "unknown",
    code: error?.code ?? null,
    message: error?.message ?? null,
  },
});

function Bridge({ children, onLoadError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [complete, setComplete] = useState(false);
  const [ready, setReady] = useState(false);

  const confirm = useCallback(
    async (kind) => {
      if (!stripe || !elements) return asError({ type: "not_ready" });
      const options = {
        elements,
        redirect: "if_required",
        confirmParams: { return_url: window.location.href },
      };
      try {
        if (kind === "setup") {
          const { error, setupIntent } = await stripe.confirmSetup(options);
          return error ? asError(error) : { ok: true, intent: setupIntent };
        }
        const { error, paymentIntent } = await stripe.confirmPayment(options);
        return error ? asError(error) : { ok: true, intent: paymentIntent };
      } catch (_) {
        return asError({ type: "api_connection_error", message: null });
      }
    },
    [stripe, elements],
  );

  const field = (
    <PaymentElement
      options={{ layout: "tabs" }}
      onReady={() => setReady(true)}
      onLoadError={onLoadError}
      onChange={(event) => setComplete(Boolean(event.complete))}
    />
  );
  return children({
    field,
    ready,
    loadError: null,
    complete: ready && complete && Boolean(stripe),
    confirm,
  });
}

export function StripeCardForm({ clientSecret, children }) {
  const [appearance, setAppearance] = useState(null);
  const [failed, setFailed] = useState(false);
  const host = useCallback((node) => {
    if (node) setAppearance(appearanceFrom(node));
  }, []);
  const [promise, setPromise] = useState(null);
  useEffect(() => {
    if (!appearance || !clientSecret) return undefined;
    let live = true;
    const attempt = stripeOnce();
    setFailed(false);
    setPromise(attempt);
    attempt.catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [appearance, clientSecret]);
  const onLoadError = useCallback(() => setFailed(true), []);
  const unavailable = () =>
    children({
      field: null,
      ready: false,
      loadError: STRIPE_UNREACHABLE,
      complete: false,
      confirm: async () => asError({ type: "not_ready" }),
    });
  return (
    <div ref={host}>
      {failed
        ? unavailable()
        : appearance &&
          clientSecret &&
          promise && (
            <Elements
              key={clientSecret}
              stripe={promise}
              options={{ clientSecret, appearance }}
            >
              <Bridge onLoadError={onLoadError}>{children}</Bridge>
            </Elements>
          )}
    </div>
  );
}

export default StripeCardForm;
